import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    Card,
    CardContent,
    Chip
} from '@mui/material';
import {
    LocalShipping as VehicleIcon,
    CheckCircle as ActiveIcon,
    Cancel as InactiveIcon
} from '@mui/icons-material';
import { useApiHandler } from '../../hooks/useApiHandler';
import DashboardLayout from '../../components/layouts/DashboardLayout';
import VehicleList from './vehicles/VehicleList';
import { vehicleService } from '../../services/vehicleService';

function VehicleManagement() {
    const [stats, setStats] = useState(null);
    const { showError } = useApiHandler();

    useEffect(() => {
        loadVehicleStats();
    }, []);

    const loadVehicleStats = async () => {
        try {
            const response = await vehicleService.getVehicleStats();

            if (response.success) {
                setStats(response.data);
            }
        } catch (error) {
            showError('Error loading vehicle statistics');
        }
    };

    const refreshStats = () => {
        loadVehicleStats();
    };

    return (
        <DashboardLayout>
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    Vehicle Management
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    Manage vehicles available for shipment tracking updates
                </Typography>
            </Box>

            {/* Statistics Cards */}
            {stats && (
                <Grid container spacing={3} sx={{ mb: 4 }}>
                    <Grid item xs={12} md={4}>
                        <Card>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <VehicleIcon color="primary" sx={{ mr: 1 }} />
                                    <Typography variant="h6">Total Vehicles</Typography>
                                </Box>
                                <Typography variant="h4" color="primary" gutterBottom>
                                    {stats.total}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Chip
                                        label={`${stats.active} Active`}
                                        color="success"
                                        size="small"
                                    />
                                    <Chip
                                        label={`${stats.inactive} Inactive`}
                                        color="default"
                                        size="small"
                                    />
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Card>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <ActiveIcon color="success" sx={{ mr: 1 }} />
                                    <Typography variant="h6">Active Vehicles</Typography>
                                </Box>
                                <Typography variant="h4" color="success.main" gutterBottom>
                                    {stats.active}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Chip
                                        label="Available for tracking"
                                        color="success"
                                        size="small"
                                    />
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Card>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <InactiveIcon color="disabled" sx={{ mr: 1 }} />
                                    <Typography variant="h6">Inactive Vehicles</Typography>
                                </Box>
                                <Typography variant="h4" color="text.secondary" gutterBottom>
                                    {stats.inactive}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Chip
                                        label="Hidden from tracking"
                                        color="default"
                                        size="small"
                                    />
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}

            {/* Vehicle Management */}
            <Paper sx={{ width: '100%' }}>
                <VehicleList onDataChange={refreshStats} />
            </Paper>
        </DashboardLayout>
    );
}

export default VehicleManagement;
